import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { 
  FileText, DollarSign, Clock, Calendar, Send, Loader2, 
  CheckCircle, Calculator, Copy, Download, Mail, Sparkles,
  AlertCircle, Building2, User
} from "lucide-react";

// Budget label mapping
const BUDGET_LABELS = {
  under_500: { label: "Under $500", min: 250, max: 500 },
  "500_1000": { label: "$500 - $1,000", min: 500, max: 1000 },
  "1000_2500": { label: "$1,000 - $2,500", min: 1000, max: 2500 },
  "2500_5000": { label: "$2,500 - $5,000", min: 2500, max: 5000 },
  "5000_10000": { label: "$5,000 - $10,000", min: 5000, max: 10000 },
  "10000_plus": { label: "$10,000+", min: 10000, max: 25000 },
  discuss: { label: "To be discussed", min: 2500, max: 10000 }
};

// Timeline mapping to weeks
const TIMELINE_WEEKS = {
  urgent: { label: "ASAP (Within 1 week)", weeks: 1 },
  short: { label: "Short-term (2-4 weeks)", weeks: 3 },
  medium: { label: "Medium-term (1-2 months)", weeks: 6 },
  long: { label: "Long-term (3+ months)", weeks: 12 },
  ongoing: { label: "Ongoing/Retainer", weeks: 4 },
  flexible: { label: "Flexible", weeks: 4 }
};

// Project type hourly estimates
const PROJECT_TYPE_HOURS = {
  business_setup: { base: 8, label: "Business Setup & Formation" },
  brand_design: { base: 20, label: "Brand & Graphic Design" },
  sop_creation: { base: 16, label: "SOP & Process Documentation" },
  website: { base: 40, label: "Website Development" },
  marketing: { base: 24, label: "Marketing Strategy" },
  social_media: { base: 12, label: "Social Media Management" },
  seo: { base: 16, label: "SEO & Content" },
  admin_support: { base: 20, label: "Admin & Operations Support" },
  media_promo: { base: 30, label: "Media & Podcast Production" },
  consulting: { base: 12, label: "Strategic Consulting" },
  other: { base: 16, label: "Other" }
};

export const QuoteBuilder = ({ open, onOpenChange, request, onQuoteSent }) => {
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Quote configuration
  const [hourlyRate, setHourlyRate] = useState(150);
  const [hoursPerDay, setHoursPerDay] = useState(5); // Default 4-6 hours, using 5 as middle
  const [totalHours, setTotalHours] = useState(0);
  const [projectFee, setProjectFee] = useState(0);
  const [depositPercent, setDepositPercent] = useState(50);
  const [validDays, setValidDays] = useState(14);
  
  // Quote content
  const [projectSummary, setProjectSummary] = useState("");
  const [deliverables, setDeliverables] = useState([]);
  const [newDeliverable, setNewDeliverable] = useState("");
  const [timeline, setTimeline] = useState("");
  const [terms, setTerms] = useState("");
  const [includePaymentPlan, setIncludePaymentPlan] = useState(true);
  
  // Calculate estimates when request changes
  useEffect(() => {
    if (request) {
      calculateEstimates();
      generateInitialContent();
    }
  }, [request]);

  // Recalculate fee when hours or rate changes
  useEffect(() => {
    setProjectFee(totalHours * hourlyRate);
  }, [totalHours, hourlyRate]);

  const calculateEstimates = () => {
    if (!request) return;
    
    // Calculate total hours based on project types
    let baseHours = 0;
    if (request.project_types && request.project_types.length > 0) {
      request.project_types.forEach(type => {
        baseHours += PROJECT_TYPE_HOURS[type]?.base || 16;
      });
    } else {
      baseHours = 24; // Default estimate
    }
    
    // Adjust based on budget expectation
    const budgetInfo = BUDGET_LABELS[request.budget_range];
    if (budgetInfo) {
      const avgBudget = (budgetInfo.min + budgetInfo.max) / 2;
      const estimatedHoursFromBudget = avgBudget / hourlyRate;
      // Use the higher of calculated or budget-implied hours
      baseHours = Math.max(baseHours, Math.round(estimatedHoursFromBudget * 0.8));
    }
    
    setTotalHours(baseHours);
  };

  const generateInitialContent = () => {
    if (!request) return;
    
    const projectTypes = request.project_types?.map(t => PROJECT_TYPE_HOURS[t]?.label || t).join(", ") || "Custom Project";
    const timelineInfo = TIMELINE_WEEKS[request.timeline];
    const weeks = timelineInfo?.weeks || 4;
    const workDays = Math.ceil(totalHours / hoursPerDay);
    
    // Generate project summary
    const summary = `Thank you for your interest in working with My Biz Consulting on your ${projectTypes.toLowerCase()} project${request.project_title ? ` "${request.project_title}"` : ""}.

Based on your project requirements and goals, we've prepared this custom proposal. Our approach focuses on delivering strategic, high-quality work within your timeline while maintaining the flexibility you need.

${request.goals ? `\nYour stated success criteria: "${request.goals}"` : ""}`;
    
    setProjectSummary(summary);
    
    // Generate deliverables based on project types
    const autoDeliverables = [];
    if (request.project_types) {
      request.project_types.forEach(type => {
        switch(type) {
          case "business_setup":
            autoDeliverables.push("Business structure consultation & recommendations");
            autoDeliverables.push("Formation documentation & filing guidance");
            break;
          case "brand_design":
            autoDeliverables.push("Brand strategy & positioning document");
            autoDeliverables.push("Logo design (3 concepts, 2 revision rounds)");
            autoDeliverables.push("Brand style guide");
            break;
          case "sop_creation":
            autoDeliverables.push("Process mapping & workflow analysis");
            autoDeliverables.push("Standard Operating Procedures documentation");
            autoDeliverables.push("Team implementation guide");
            break;
          case "website":
            autoDeliverables.push("Website strategy & sitemap");
            autoDeliverables.push("Custom website design & development");
            autoDeliverables.push("Mobile-responsive implementation");
            autoDeliverables.push("Launch support & training");
            break;
          case "marketing":
            autoDeliverables.push("Marketing strategy document");
            autoDeliverables.push("Campaign planning & content calendar");
            autoDeliverables.push("Implementation roadmap");
            break;
          case "social_media":
            autoDeliverables.push("Social media strategy & audit");
            autoDeliverables.push("Content templates & posting schedule");
            break;
          case "seo":
            autoDeliverables.push("SEO audit & keyword research");
            autoDeliverables.push("Content optimization recommendations");
            break;
          case "consulting":
            autoDeliverables.push("Strategic assessment & recommendations");
            autoDeliverables.push("Action plan with priorities");
            break;
          default:
            autoDeliverables.push("Project deliverables per scope discussion");
        }
      });
    }
    setDeliverables(autoDeliverables.length > 0 ? autoDeliverables : ["Project deliverables to be defined"]);
    
    // Generate timeline
    const timelineText = `Project Duration: Approximately ${weeks} week${weeks > 1 ? 's' : ''}
Work Schedule: ${hoursPerDay} hours per day dedicated to your project
Estimated Completion: ${new Date(Date.now() + weeks * 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}

Weekly check-ins and progress updates included. Timeline may adjust based on feedback cycles and client responsiveness.`;
    
    setTimeline(timelineText);
    
    // Set standard terms
    setTerms(`Payment Terms:
• ${depositPercent}% deposit required to begin work
• Remaining balance due upon project completion
• Payment via Stripe (credit card) or bank transfer

Revision Policy:
• Two rounds of revisions included per deliverable
• Additional revisions billed at hourly rate

Communication:
• Primary communication via email with 24-48 hour response
• Weekly progress calls (30 min) included
• Slack/messaging access for quick questions

This quote is valid for ${validDays} days from date of issue.`);
  };

  const addDeliverable = () => {
    if (newDeliverable.trim()) {
      setDeliverables([...deliverables, newDeliverable.trim()]);
      setNewDeliverable("");
    }
  };

  const removeDeliverable = (index) => {
    setDeliverables(deliverables.filter((_, i) => i !== index));
  };

  const generateQuoteText = () => {
    const deposit = Math.round(projectFee * (depositPercent / 100));
    const finalPayment = projectFee - deposit;
    const weeks = TIMELINE_WEEKS[request?.timeline]?.weeks || 4;
    
    return `
═══════════════════════════════════════════════════════════════
                    PROJECT PROPOSAL & QUOTE
                      My Biz Consulting
═══════════════════════════════════════════════════════════════

Prepared for: ${request?.name}
${request?.company_name ? `Company: ${request.company_name}` : ""}
Date: ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
Quote #: Q-${Date.now().toString().slice(-8)}
Valid for: ${validDays} days

───────────────────────────────────────────────────────────────
PROJECT OVERVIEW
───────────────────────────────────────────────────────────────

${projectSummary}

───────────────────────────────────────────────────────────────
DELIVERABLES
───────────────────────────────────────────────────────────────

${deliverables.map((d, i) => `${i + 1}. ${d}`).join('\n')}

───────────────────────────────────────────────────────────────
TIMELINE
───────────────────────────────────────────────────────────────

${timeline}

───────────────────────────────────────────────────────────────
INVESTMENT
───────────────────────────────────────────────────────────────

Project Fee:           $${projectFee.toLocaleString()}
  • Estimated Hours:   ${totalHours} hours
  • Hourly Rate:       $${hourlyRate}/hour
  • Daily Capacity:    ${hoursPerDay} hours/day dedicated to your project

Payment Schedule:
  • Deposit (${depositPercent}%):       $${deposit.toLocaleString()} (due to begin)
  • Final Payment:     $${finalPayment.toLocaleString()} (due upon completion)
${includePaymentPlan ? `
💳 PAYMENT PLAN AVAILABLE
Split your investment into manageable payments via FinanceMutual.
Request details when accepting this proposal.` : ""}

───────────────────────────────────────────────────────────────
TERMS & CONDITIONS
───────────────────────────────────────────────────────────────

${terms}

───────────────────────────────────────────────────────────────
NEXT STEPS
───────────────────────────────────────────────────────────────

To accept this proposal:
1. Reply to this email confirming acceptance
2. We'll send a secure payment link for the deposit
3. Schedule kickoff call within 48 hours of deposit
4. Project begins!

Questions? Reply to this email or schedule a quick call.

───────────────────────────────────────────────────────────────

Thank you for considering My Biz Consulting for your project.
We look forward to helping you achieve your goals!

Best regards,
My Biz Consulting Team
support@mybizconsulting.net

═══════════════════════════════════════════════════════════════
`;
  };

  const copyToClipboard = async () => {
    const quoteText = generateQuoteText();
    await navigator.clipboard.writeText(quoteText);
    setCopied(true);
    toast.success("Quote copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const sendQuoteEmail = async () => {
    setSending(true);
    try {
      const quoteText = generateQuoteText();
      const deposit = Math.round(projectFee * (depositPercent / 100));
      
      const response = await authFetch(`${API}/consultant/project-scope-requests/${request.request_id}/send-quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          quote_text: quoteText,
          project_fee: projectFee,
          deposit_amount: deposit,
          total_hours: totalHours,
          hourly_rate: hourlyRate
        })
      });
      
      if (response.ok) {
        toast.success("Quote sent successfully!");
        onQuoteSent?.();
        onOpenChange(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send quote");
      }
    } catch (error) {
      toast.error("Error sending quote");
      console.error(error);
    } finally {
      setSending(false);
    }
  };

  if (!request) return null;

  const deposit = Math.round(projectFee * (depositPercent / 100));
  const finalPayment = projectFee - deposit;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-green-600" />
            Quote Builder
          </DialogTitle>
          <DialogDescription>
            Create a professional proposal for {request.name}
            {request.company_name && ` at ${request.company_name}`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 pr-2">
          {/* Pricing Calculator */}
          <Card className="border-green-200 bg-green-50/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calculator className="w-5 h-5 text-green-600" />
                Pricing Calculator
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs text-neutral-500">Hourly Rate ($)</Label>
                  <Input
                    type="number"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(Number(e.target.value))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-neutral-500">Hours/Day (4-6 recommended)</Label>
                  <Select value={hoursPerDay.toString()} onValueChange={(v) => setHoursPerDay(Number(v))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">4 hours/day</SelectItem>
                      <SelectItem value="5">5 hours/day</SelectItem>
                      <SelectItem value="6">6 hours/day</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-neutral-500">Total Hours</Label>
                  <Input
                    type="number"
                    value={totalHours}
                    onChange={(e) => setTotalHours(Number(e.target.value))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-neutral-500">Deposit %</Label>
                  <Select value={depositPercent.toString()} onValueChange={(v) => setDepositPercent(Number(v))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25%</SelectItem>
                      <SelectItem value="50">50%</SelectItem>
                      <SelectItem value="75">75%</SelectItem>
                      <SelectItem value="100">100% upfront</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-neutral-600">
                    <strong>Client's Budget:</strong> {BUDGET_LABELS[request.budget_range]?.label || "Not specified"}
                  </p>
                  <p className="text-sm text-neutral-600">
                    <strong>Timeline:</strong> {TIMELINE_WEEKS[request.timeline]?.label || request.timeline}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-green-700">${projectFee.toLocaleString()}</p>
                  <p className="text-sm text-neutral-500">
                    Deposit: ${deposit.toLocaleString()} | Final: ${finalPayment.toLocaleString()}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="paymentPlan"
                  checked={includePaymentPlan}
                  onCheckedChange={setIncludePaymentPlan}
                />
                <Label htmlFor="paymentPlan" className="text-sm cursor-pointer">
                  Include payment plan option (FinanceMutual)
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Project Summary */}
          <div className="space-y-2">
            <Label>Project Summary</Label>
            <Textarea
              value={projectSummary}
              onChange={(e) => setProjectSummary(e.target.value)}
              rows={5}
              placeholder="Personalized project summary..."
            />
          </div>

          {/* Deliverables */}
          <div className="space-y-2">
            <Label>Deliverables</Label>
            <div className="space-y-2">
              {deliverables.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-neutral-50 p-2 rounded-lg">
                  <span className="text-sm font-medium text-neutral-500 w-6">{idx + 1}.</span>
                  <span className="flex-1 text-sm">{item}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeDeliverable(idx)}
                    className="text-red-500 hover:text-red-700 h-6 px-2"
                  >
                    ×
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newDeliverable}
                onChange={(e) => setNewDeliverable(e.target.value)}
                placeholder="Add a deliverable..."
                onKeyDown={(e) => e.key === "Enter" && addDeliverable()}
              />
              <Button onClick={addDeliverable} variant="outline" size="sm">
                Add
              </Button>
            </div>
          </div>

          {/* Timeline */}
          <div className="space-y-2">
            <Label>Timeline & Schedule</Label>
            <Textarea
              value={timeline}
              onChange={(e) => setTimeline(e.target.value)}
              rows={4}
            />
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Note: Work is limited to {hoursPerDay} hours/day to ensure quality and sustainability
            </p>
          </div>

          {/* Terms */}
          <div className="space-y-2">
            <Label>Terms & Conditions</Label>
            <Textarea
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              rows={6}
            />
          </div>

          {/* Quote Validity */}
          <div className="flex items-center gap-4">
            <Label className="whitespace-nowrap">Quote valid for</Label>
            <Select value={validDays.toString()} onValueChange={(v) => setValidDays(Number(v))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="14">14 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Separator className="my-4" />

        <DialogFooter className="flex-shrink-0">
          <div className="flex items-center gap-3 w-full justify-between">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={copyToClipboard}
                disabled={copied}
              >
                {copied ? (
                  <><CheckCircle className="w-4 h-4 mr-1 text-green-600" /> Copied!</>
                ) : (
                  <><Copy className="w-4 h-4 mr-1" /> Copy Quote</>
                )}
              </Button>
              <Button
                onClick={sendQuoteEmail}
                disabled={sending}
                className="bg-green-600 hover:bg-green-700"
              >
                {sending ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-1" /> Send to {request.name.split(' ')[0]}</>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteBuilder;
